import React, { useEffect } from "react";
import { setNativeKeyboardAvoid } from "@/utils/nativeBridge";
import { MButton } from "@/components/ui/atoms/MButtons";
import { motion, AnimatePresence } from "framer-motion";
import FooterDrawerDoneButton from "@/components/ui/atoms/FooterDrawerDoneButton";
import "@/components/ui/atoms/footer-drawer-done-button.css";
import { IconX } from "@tabler/icons-react";
import styles from "./FooterDrawer.module.css";
import DrawerPortal from "./DrawerPortal";
import MWrap from "@/components/ui/atoms/MWrap";

interface FooterDrawerProps {
  open: boolean;
  onClose: () => void;
  showClose?: boolean;
  children: React.ReactNode;
  doneDisabled?: boolean;
  onDone?: () => void;
  doneText?: string;
  donePosition?: "top" | "bottom";
  title?: string;
  subtitle?: string;
  titleAlign?: "left" | "center";
  /**
   * Height of the drawer (e.g. 'auto', '400px', '80%').
   * If not provided, defaults to maxHeight: '85vh'.
   */
  height?: string;
  className?: string;
  /**
   * Allow keyboard interaction for input fields (prevents blur and allows smooth keyboard opening)
   */
  allowKeyboard?: boolean;
  /** Pass a higher z-index so this drawer's portal container sits above MDialog (e.g. date/time pickers). */
  containerZIndex?: number;
}

const backdropVariants = {
  visible: { opacity: 1, pointerEvents: "auto" as const },
  hidden: { opacity: 0, pointerEvents: "none" as const },
};

const drawerVariants = {
  hidden: { y: "100%" },
  visible: { y: 0 },
};

const FooterDrawer: React.FC<FooterDrawerProps> = ({
  open,
  onClose,
  showClose = true,
  children,
  doneDisabled,
  onDone,
  doneText,
  donePosition,
  title,
  subtitle,
  titleAlign = "center",
  height,
  className,
  allowKeyboard = false,
  containerZIndex,
}) => {
  // Detect Android device
  const isAndroid = /Android/i.test(navigator.userAgent);

  useEffect(() => {
    if (open) {
      if (!allowKeyboard && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      document.body.classList.add("drawer-open");
    } else {
      document.body.classList.remove("drawer-open");
    }

    return () => {
      document.body.classList.remove("drawer-open");
    };
  }, [open, allowKeyboard]);

  useEffect(() => {
    if (!allowKeyboard) return;
    setNativeKeyboardAvoid(!open);
    return () => setNativeKeyboardAvoid(true);
  }, [open, allowKeyboard]);

  // Scroll focused input into view within the drawer's content div.
  // iOS won't auto-scroll fixed-position drawers, so we handle it manually.
  useEffect(() => {
    if (!open || !allowKeyboard) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement
        )
      )
        return;
      setTimeout(() => {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 300);
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => document.removeEventListener("focusin", handleFocusIn);
  }, [open, allowKeyboard]);

  return (
    <AnimatePresence>
      {open && (
        <DrawerPortal containerZIndex={containerZIndex}>
          {/* Backdrop */}
          <motion.div
            className={styles.backdrop}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            onClick={onClose}
          />
          {/* Drawer */}
          <div className={styles.drawerWrapper}>
            <motion.div
              className={`${styles.drawer} ${className || ""}`}
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={drawerVariants}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              style={height ? { height } : { maxHeight: "85vh" }}
            >
              {/* Header */}
              <div
                className={`${styles.header} ${
                  subtitle ? styles["header--noBottomBorder"] : ""
                }`}
              >
                <div className={styles.dragBar} />
                <div
                  className={`${styles.headerContent} ${
                    titleAlign === "left" ? styles["headerContent--left"] : ""
                  }`}
                >
                  {titleAlign !== "left" &&
                    (onDone && showClose ? (
                      <button
                        className={styles.closeButton}
                        onClick={(e) => {
                          e.preventDefault();
                          onClose();
                        }}
                        aria-label="Close"
                      >
                        <IconX size={24} />
                      </button>
                    ) : (
                      <div className={styles.spacer} />
                    ))}
                  {title && (
                    <span
                      className={`${styles.title} ${
                        titleAlign === "left" ? styles["title--left"] : ""
                      }`}
                    >
                      {typeof title === "string" ? (
                        <MWrap text={title} maxChars={30} />
                      ) : (
                        title
                      )}
                    </span>
                  )}
                  {onDone ? (
                    <div className={styles.doneButton}>
                      <MButton
                        variant="link"
                        size="md"
                        text={typeof doneText === "string" ? doneText : "Done"}
                        state="default"
                        style={{
                          padding: 0,
                          background: "none",
                          fontWeight: 600,
                          fontSize: "var(--typography-font-size-body-base)",
                        }}
                        onClick={onDone}
                        className="clear"
                        disabled={!!doneDisabled}
                      />
                    </div>
                  ) : showClose ? (
                    <button
                      className={styles.closeButton}
                      onClick={(e) => {
                        e.preventDefault();
                        onClose();
                      }}
                      aria-label="Close"
                    >
                      <IconX size={24} />
                    </button>
                  ) : (
                    <div className={styles.spacer} />
                  )}
                </div>
              </div>
              {/* Subtitle — shown directly below the header, before scrollable content */}
              {subtitle && (
                <div className={styles.subtitle}>
                  <p className={styles.subtitleText}>{subtitle}</p>
                </div>
              )}
              {/* Content */}
              <div
                className={styles.content}
                style={{ paddingBottom: isAndroid ? "40px" : "15px" }}
              >
                {children}
                {onDone && donePosition !== "top" && (
                  <div className="w-full bg-white border-t border-gray-200 p-4">
                    <FooterDrawerDoneButton
                      disabled={doneDisabled}
                      onClick={onDone}
                    >
                      {typeof doneText === "string" ? doneText : "Done"}
                    </FooterDrawerDoneButton>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </DrawerPortal>
      )}
    </AnimatePresence>
  );
};

export default FooterDrawer;
export { FooterDrawerDoneButton };
